/**
 * Invoice and credit note Vuex module for Portal Application
 * Controls the data and actions for invoices
 * 
 * @module Invoices
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date August 2023
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_INVOICE = {
    invoiceFound: null,
    header: {},
    lines: [],
};

const DEFAULT_STATE = {
    invoice: DEFAULT_INVOICE,
    invoiceList: [],
    storeInvoiceNumbers: [],
    invoiceTypes: [],
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getInvoice(st) {
        return st.invoice;
    },

    getInvoiceList(st) {
        return st.invoiceList;
    },

    getStoreInvoiceNumbers(st) {
        return st.storeInvoiceNumbers;
    },

    getInvoiceTypes(st) {
        return st.invoiceTypes;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },
    
    setInvoice(st, payload) {
        st.invoice = payload;
    },
    
    setInvoiceList(st, payload) {
        st.invoiceList = payload;
    },
    
    setStoreInvoiceNumbers(st, payload) {
        st.storeInvoiceNumbers = payload;
    },

    setInvoiceTypes(st, payload) {
        st.invoiceTypes = payload;
    },
};

const actions = {
    // Get all Invoices
    async fetchInvoices({ commit }, payload) 
    {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 20),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "invoiceNumber", state: "desc"}),
        };

        const response = await portalapi.moduleInvoices.getInvoicesPage(requestPayload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setInvoiceList", response.data);
        }
    },


    // Get and save Invoice into VueX store
    async fetchInvoice({ commit }, invoiceNumber) 
    {
        commit("setInvoice", DEFAULT_INVOICE);   

        var invoice = await actions.retrieveInvoice({ commit }, invoiceNumber);

        commit("setInvoice", invoice);
    },


    // Get and return Invoice
    async retrieveInvoice({ commit }, invoiceNum) 
    {
        let response = await portalapi.moduleInvoices.getInvoice(invoiceNum);

        // Check if the invoice was found
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            response = response.data;
            response.invoiceFound = true;
        } else {
            response = {
                invoiceFound: false,
            };
        }

        return response;
    },


    // Adds items from invoice to Print Label Queue
    async addInvoiceItemsToPrintLabelQueue({ commit }, invoiceNumber) 
    {
        return await portalapi.moduleInvoices.addInvoiceItemsToLabelCart(invoiceNumber);
    },


    // Get Invoices for logged in user store.
    async fetchStoreInvoiceNumbers({ commit }) 
    {
        const response = await portalapi.moduleInvoices.getStoreInvoiceNumbers();

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setStoreInvoiceNumbers", response.data);
        }
    },
    

    // Get invoice types
    async fetchInvoiceTypes(context) 
    {
        let commit = context.commit;

        if (context.state.invoiceTypes.length == 0) {
            const response = await portalapi.moduleInvoices.getInvoiceTypes();

            if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setInvoiceTypes", response.data);
            } 
        }
    },


    // Get invoice PDF from D365
    async retrieveInvoicePDF(context, invoiceNumber)
    {
        // Retrieve file
        let response = await portalapi.moduleInvoices.getInvoicePdf(invoiceNumber);

        // Successfully retrieved PDF file
        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            return response.data;
        } 
        // Invoice PDF not found
        else if (response.response && response.response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.NOT_FOUND) {
            // Parse the ArrayBuffer response to get the error message
            let errorMessage = 'Invoice PDF not found.';
            try {
                if (response.response.data) {
                    const decoder = new TextDecoder('utf-8');
                    const text = decoder.decode(response.response.data);
                    const errorData = JSON.parse(text);
                    errorMessage = errorData.message || errorMessage;
                }
            } catch (e) {
                console.error('Error parsing error response:', e);
            }
            
            vm.config.globalProperties.$toast.open({
                message: errorMessage,
                type: 'error',
            });
        } else {
            vm.config.globalProperties.$toast.open({
                message: 'Error retrieving invoice PDF.',
                type: 'error',
            });
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};